/**
 * Web Audio sound effects utility for POS.
 * Generates synthetic sounds (cash register bell and warning beeps)
 * without needing external audio files.
 */

let sharedAudioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtxClass) return null;
      sharedAudioCtx = new AudioCtxClass();
    }
    if (sharedAudioCtx.state === 'suspended') {
      sharedAudioCtx.resume().catch(() => {});
    }
    return sharedAudioCtx;
  } catch (err) {
    console.warn('Web Audio API not supported or blocked:', err);
    return null;
  }
}

/**
 * Play a crisp, metallic cash register / service bell chime ("Ding!")
 * triggered when an item is selected or added on the POS page.
 */
export function playBellSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;

    // Harmonic frequencies for a bright metallic bell sound
    const tones = [
      { freq: 1568, gain: 0.35, decay: 0.45 }, // G6 fundamental
      { freq: 2093, gain: 0.25, decay: 0.35 }, // C7 harmonic
      { freq: 3136, gain: 0.15, decay: 0.25 }, // G7 sparkle
    ];

    tones.forEach(({ freq, gain: targetGain, decay }) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);

      // Fast attack, exponential decay for bell resonance
      gainNode.gain.setValueAtTime(0.001, now);
      gainNode.gain.linearRampToValueAtTime(targetGain, now + 0.006);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + decay);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.start(now);
      osc.stop(now + decay);
    });
  } catch (e) {
    console.warn('Failed to play bell sound:', e);
  }
}

/**
 * Play a single short warning beep.
 */
export function playSingleBeep(freq = 950, duration = 0.12): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, now);

    gainNode.gain.setValueAtTime(0.01, now);
    gainNode.gain.linearRampToValueAtTime(0.28, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + duration);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + duration);
  } catch (e) {
    console.warn('Failed to play beep sound:', e);
  }
}

/**
 * Start a continuous repeating warning beep until explicitly stopped.
 * Used for age restriction / verification alerts.
 */
export function startContinuousBeep(intervalMs = 600): { stop: () => void } {
  let isStopped = false;

  // Play initial beep immediately
  playSingleBeep(920, 0.14);

  const timer = setInterval(() => {
    if (isStopped) {
      clearInterval(timer);
      return;
    }
    playSingleBeep(920, 0.14);
  }, intervalMs);

  return {
    stop: () => {
      isStopped = true;
      clearInterval(timer);
    },
  };
}
